import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Snackbar,
  LinearProgress,
  AppBar,
  Toolbar,
  Button,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { 
  Home as HomeIcon, 
  Search as SearchIcon, 
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ApiResponse, FeasibilityReport } from '../../types/api.types';
import { getApiUrl } from '../../utils/api';
import AddressSearch from '../../components/AddressSearch';
import PropertyOverview from '../../components/PropertyOverview';
import ZoningAnalysisCard from '../../components/ZoningAnalysisCard';
import DevelopmentOptions from '../../components/DevelopmentOptions';
import MarketAnalysisCard from '../../components/MarketAnalysisCard';
import FinancialSummary from '../../components/FinancialSummary';
import FloorPlanViewer from '../../components/FloorPlanViewer';
import ExecutiveSummaryCard from '../../components/ExecutiveSummaryCard';
import ErrorBoundary from '../../components/ErrorBoundary';
import LoadingState from '../../components/LoadingState';
import EmptyState from '../../components/EmptyState';
import DataQualityIndicator from '../../components/DataQualityIndicator';

// This component wraps the existing property analyzer functionality
// maintaining the exact same UI and behavior, just wrapped in the new auth system

const PropertyAnalyzerPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [report, setReport] = useState<FeasibilityReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNewSearch = () => {
    setReport(null);
    setError(null);
    setIsSaved(false);
  };

  const handleSaveProperty = async () => {
    if (!report || !report.property_data) return;
    
    setSavingProperty(true);
    
    try {
      const response = await fetch(getApiUrl('/api/users/properties/save'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          address: report.property_data.address,
          property_data: report.property_data,
          notes: '',
          tags: [],
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsSaved(true);
        setError(null);
      } else {
        setError(data.message || 'Failed to save property');
      }
    } catch (err) {
      setError('Failed to save property');
    } finally {
      setSavingProperty(false);
    }
  };

  const handleAddressSubmit = async (address: string, city: string) => {
    setLoading(true);
    setError(null);
    setReport(null);
    setIsSaved(false);
    
    const fullAddress = `${address}, ${city}, MA`;
    
    try {
      const response = await fetch(`${getApiUrl('/api/feasibility')}?address=${encodeURIComponent(fullAddress)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      const data: ApiResponse = await response.json();
      
      if (data.success && data.report) {
        // Validate and ensure all required fields exist
        const validatedReport: FeasibilityReport = {
          ...data.report,
          property_data: data.report.property_data || {},
          zoning_analysis: data.report.zoning_analysis || {},
          buildable_envelope: data.report.buildable_envelope || {},
          development_options: Array.isArray(data.report.development_options) 
            ? data.report.development_options 
            : [],
          recommended_option: data.report.recommended_option || '',
          market_analysis: data.report.market_analysis || {},
          floor_plans: data.report.floor_plans || {},
          executive_summary: data.report.executive_summary || {},
          risks: Array.isArray(data.report.risks) ? data.report.risks : [],
          next_steps: Array.isArray(data.report.next_steps) ? data.report.next_steps : [],
          generated_at: data.report.generated_at || new Date().toISOString(),
          data_quality: data.report.data_quality || {},
        };
        
        // Ensure nested objects have required fields
        if (validatedReport.property_data) {
          validatedReport.property_data.building = validatedReport.property_data.building || {};
          validatedReport.property_data.geometry = validatedReport.property_data.geometry || {};
          validatedReport.property_data.assessment = validatedReport.property_data.assessment || {};
        }
        
        setReport(validatedReport);
      } else {
        throw new Error(data.error || 'Failed to generate report');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('Error fetching feasibility report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setReport(null);
    setError(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation Bar */}
      <AppBar position="static" color="primary" elevation={1}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <HomeIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            QuickBuildr.ai - Property Analyzer
          </Typography>
          <Button
            color="inherit"
            startIcon={<DashboardIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Dashboard
          </Button>
          {report && (
            <>
              <Button
                color="inherit"
                startIcon={<SearchIcon />}
                onClick={handleNewSearch}
                sx={{ mr: 2 }}
              >
                New Search
              </Button>
              <Button
                color="inherit"
                startIcon={isSaved ? <CheckIcon /> : <SaveIcon />}
                onClick={handleSaveProperty}
                disabled={savingProperty || isSaved}
                sx={{ mr: 2 }}
              >
                {savingProperty ? 'Saving...' : isSaved ? 'Saved' : 'Save Property'}
              </Button>
            </>
          )}
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {loading && <LinearProgress sx={{ position: 'fixed', top: 64, left: 0, right: 0, zIndex: 9999 }} />}
      
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {!report ? (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                Property Feasibility Analysis
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Comprehensive real estate development analysis for Massachusetts properties
              </Typography>
            </Box>
            
            <AddressSearch onSubmit={handleAddressSubmit} loading={loading} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Quick Actions Bar */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/dashboard')}
                >
                  Back to Dashboard
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SearchIcon />}
                  onClick={handleNewSearch}
                >
                  Search New Property
                </Button>
              </Box>
            </Grid>

            {/* Header Section */}
            <Grid item xs={12}>
              <ErrorBoundary>
                <PropertyOverview data={report.property_data} />
              </ErrorBoundary>
            </Grid>

            {/* Data Quality Indicator */}
            <Grid item xs={12}>
              <DataQualityIndicator quality={report.data_quality} />
            </Grid>

            {/* Executive Summary */}
            <Grid item xs={12}>
              <ExecutiveSummaryCard summary={report.executive_summary} />
            </Grid>

            {/* Main Analysis Grid */}
            <Grid item xs={12} lg={6}>
              <ZoningAnalysisCard 
                zoning={report.zoning_analysis} 
                envelope={report.buildable_envelope} 
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <MarketAnalysisCard market={report.market_analysis} />
            </Grid>

            {/* Development Options */}
            <Grid item xs={12}>
              <ErrorBoundary>
                <DevelopmentOptions 
                  options={report.development_options}
                  recommended={report.recommended_option}
                />
              </ErrorBoundary>
            </Grid>

            {/* Financial Summary */}
            <Grid item xs={12}>
              <ErrorBoundary>
                <FinancialSummary 
                  options={report.development_options}
                  taxAnalysis={report.tax_analysis}
                />
              </ErrorBoundary>
            </Grid>

            {/* Floor Plans */}
            {report.floor_plans && (
              <Grid item xs={12}>
                <FloorPlanViewer plans={report.floor_plans} />
              </Grid>
            )}

            {/* Risks and Next Steps */}
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: 'background.paper', p: 3, borderRadius: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'warning.main' }}>
                  Key Risks
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  {(report.risks || []).map((risk, index) => (
                    <Typography key={index} component="li" variant="body2" sx={{ mb: 1 }}>
                      {risk}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: 'background.paper', p: 3, borderRadius: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'success.main' }}>
                  Next Steps
                </Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  {(report.next_steps || []).map((step, index) => (
                    <Typography key={index} component="li" variant="body2" sx={{ mb: 1 }}>
                      {step}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Report Generation Info */}
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                <Typography variant="caption">
                  Report generated on {new Date(report.generated_at).toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PropertyAnalyzerPage;